// Configuración — DATOS REALES: tu cuenta, tus roles con la entidad a la que aplican, tu vista
// activa y la configuración contable (cuenta del PUC asignada a cada concepto). Antes mostraba
// usuarios, roles y montos de ejemplo. La gestión de usuarios y roles vive en
// «Administración de la Red → Usuarios y roles».

import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FormularioDialog from '../components/FormularioDialog';
import TablaDatos from '../components/TablaDatos';
import useCargar from '../hooks/useCargar';
import useCooperativaOperativa from '../hooks/useCooperativaOperativa';
import useRedReal from '../hooks/useRedReal';
import { useAuth } from '../context/AuthContext';
import { contabilidadApi } from '../services/coopnetApi';

const Dato = ({ titulo, valor }) => (valor ? (
  <Box><Typography variant="caption" color="text.secondary">{titulo}</Typography><Typography variant="body2">{valor}</Typography></Box>
) : null);

function ConceptosContables({ id, puedeEditar }) {
  const { datos, cargando, error, recargar } = useCargar(() => contabilidadApi.conceptos(id), [id], []);
  const { datos: plan } = useCargar(() => contabilidadApi.planCuentas(id), [id], []);
  const [edit, setEdit] = useState(null);
  const nombre = useMemo(() => new Map(plan.map((p) => [p.codigo, p.nombre])), [plan]);
  if (cargando) return <CircularProgress size={24} />;
  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Alert severity="warning" sx={{ mb: 2 }}>Esta configuración es común a todas las cooperativas: un cambio afecta los comprobantes futuros de toda la red.</Alert>
      <TablaDatos
        filas={datos}
        vacio="No hay conceptos configurados."
        columnas={[
          { titulo: 'Concepto', render: (c) => c.nombre },
          { titulo: 'Cuenta asignada', render: (c) => `${c.cuentaCodigo} — ${nombre.get(c.cuentaCodigo) || ''}` },
          ...(puedeEditar ? [{ titulo: '', render: (c) => <Button size="small" onClick={() => setEdit(c)}>Cambiar cuenta</Button> }] : []),
        ]}
      />
      <FormularioDialog
        abierto={Boolean(edit)}
        titulo={`Cuenta para «${edit?.nombre ?? ''}»`}
        descripcion={`Actual: ${edit?.cuentaCodigo ?? ''}`}
        campos={[{ campo: 'nuevaCuenta', label: 'Código de la nueva cuenta (PUC)', tipo: 'select', requerido: true, opciones: plan.map((p) => ({ valor: p.codigo, etiqueta: `${p.codigo} — ${p.nombre}` })) }]}
        inicial={{ nuevaCuenta: edit?.cuentaCodigo }}
        textoBoton="Guardar"
        onGuardar={async (v) => { await contabilidadApi.actualizarConcepto(id, edit.id, v.nuevaCuenta); recargar(); }}
        onClose={() => setEdit(null)}
      />
    </>
  );
}

const Configuracion = () => {
  const { user, vistas, setSelectorAbierto, hasAnyRole } = useAuth();
  const red = useRedReal();
  const op = useCooperativaOperativa();
  const navigate = useNavigate();

  const nombreEntidad = (r) =>
    r.cooperativaId ? red.cooperativas.find((c) => c.id === r.cooperativaId)?.nombre
    : r.federacionId ? red.federaciones.find((f) => f.id === r.federacionId)?.nombre
    : r.confederacionId ? red.confederaciones.find((c) => c.id === r.confederacionId)?.nombre
    : 'Toda la red';

  const puedeVerContable = hasAnyRole(['SuperAdmin', 'AdminCooperativa', 'Contador']);
  const puedeAdministrar = hasAnyRole(['SuperAdmin', 'AdminConfederacion', 'AdminFederacion', 'AdminCooperativa']);

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>Configuración</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>Tu cuenta, tus roles y la configuración contable</Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 3, mb: 3 }}>
        <Card><CardContent>
          <Typography variant="h6" gutterBottom>Mi cuenta</Typography>
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            <Dato titulo="Nombre" valor={user?.nombreCompleto} />
            <Dato titulo="Correo" valor={user?.email} />
            <Dato titulo="Documento" valor={user?.documento} />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Tu identidad y contraseña se gestionan en el portal del ecosistema (Ecolab).
          </Typography>
        </CardContent></Card>

        <Card><CardContent>
          <Typography variant="h6" gutterBottom>Mis roles</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            {user?.esSuperAdmin && <Chip color="secondary" label="SuperAdmin · toda la red" />}
            {(user?.roles || []).map((r) => <Chip key={r.asignacionId} variant="outlined" label={`${r.rol} · ${nombreEntidad(r) || 'entidad'}`} />)}
            {!user?.esSuperAdmin && (user?.roles || []).length === 0 && <Typography variant="body2" color="text.secondary">No tienes roles asignados.</Typography>}
          </Box>
          {vistas.length > 1 && <Button size="small" onClick={() => setSelectorAbierto(true)}>Cambiar de vista</Button>}
          {puedeAdministrar && <Button size="small" onClick={() => navigate('/administracion')}>Administrar usuarios y roles</Button>}
        </CardContent></Card>
      </Box>

      {puedeVerContable && (
        <Card><CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Typography variant="h6">Cuentas contables por concepto</Typography>
            {op.seleccionable && (
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel>Cooperativa</InputLabel>
                <Select label="Cooperativa" value={op.cooperativaId || ''} onChange={(e) => op.elegir(e.target.value)}>
                  {op.opciones.map((c) => <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>)}
                </Select>
              </FormControl>
            )}
          </Box>
          {op.cooperativaId ? (
            <ConceptosContables key={op.cooperativaId} id={op.cooperativaId} puedeEditar={hasAnyRole(['SuperAdmin', 'Contador'])} />
          ) : (
            <Alert severity="info">Selecciona una cooperativa para consultar la configuración contable.</Alert>
          )}
        </CardContent></Card>
      )}
    </Box>
  );
};

export default Configuracion;
